'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, Hash, Type } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { utf8EncodeContent } from '../../data/utf8-encode-content';

const sampleText = `Hello, こんにちは, 你好, مرحبًا, नमस्ते, Здравствуйте!`;
const sampleEncoded = 'Hello, こんにちは, 你好, مرحبًا, नमस्ते, Здравствуйте!';

function encodeUtf8(text: string): string {
    return Array.from(text)
        .map(char => {
            const code = char.charCodeAt(0);
            if (code < 0x80) return char; // ASCII
            if (code < 0x800) {
                return String.fromCharCode(0xc0 | (code >> 6)) +
                    String.fromCharCode(0x80 | (code & 0x3f));
            }
            if (code < 0x10000) {
                return String.fromCharCode(0xe0 | (code >> 12)) +
                    String.fromCharCode(0x80 | ((code >> 6) & 0x3f)) +
                    String.fromCharCode(0x80 | (code & 0x3f));
            }
            // Handle surrogate pairs for characters outside the BMP (Basic Multilingual Plane)
            const u = code - 0x10000;
            return String.fromCharCode(0xf0 | (u >> 18)) +
                String.fromCharCode(0x80 | ((u >> 12) & 0x3f)) +
                String.fromCharCode(0x80 | ((u >> 6) & 0x3f)) +
                String.fromCharCode(0x80 | (u & 0x3f));
        })
        .join('');
}

function decodeUtf8(encoded: string): string {
    let result = '';
    let i = 0;

    while (i < encoded.length) {
        const c1 = encoded.charCodeAt(i++);

        // 1 byte
        if (c1 < 0x80) {
            result += String.fromCharCode(c1);
        }
        // 2 bytes
        else if (c1 > 0xBF && c1 < 0xE0) {
            const c2 = encoded.charCodeAt(i++);
            result += String.fromCharCode((c1 & 0x1F) << 6 | c2 & 0x3F);
        }
        // 3 bytes
        else if (c1 > 0xDF && c1 < 0xF0) {
            const c2 = encoded.charCodeAt(i++);
            const c3 = encoded.charCodeAt(i++);
            result += String.fromCharCode(
                (c1 & 0x0F) << 12 |
                (c2 & 0x3F) << 6 |
                c3 & 0x3F
            );
        }
        // 4 bytes (surrogate pair)
        else {
            const c2 = encoded.charCodeAt(i++);
            const c3 = encoded.charCodeAt(i++);
            const c4 = encoded.charCodeAt(i++);

            // Calculate the code point
            const codePoint = (
                ((c1 & 0x07) << 18) |
                ((c2 & 0x3F) << 12) |
                ((c3 & 0x3F) << 6) |
                (c4 & 0x3F)
            ) - 0x10000;

            // Create a surrogate pair
            result += String.fromCharCode(
                0xD800 + (codePoint >> 10),
                0xDC00 + (codePoint & 0x3FF)
            );
        }
    }

    return result;
}

export default function Utf8Tool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [activeTab, setActiveTab] = useState('encode');
    const [isCopied, setIsCopied] = useState(false);
    const [error, setError] = useState('');
    const { toast } = useToast();

    useEffect(() => {
        if (input.trim() === '') {
            setOutput('');
            setError('');
            return;
        }

        try {
            if (activeTab === 'encode') {
                const encoded = encodeUtf8(input);
                setOutput(encoded);
                setError('');
            } else {
                const decoded = decodeUtf8(input);
                setOutput(decoded);
                setError('');
            }
        } catch (error) {
            setError('Error: Invalid input for decoding');
            setOutput('');
        }
    }, [input, activeTab]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The text has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const handleSwitch = () => {
        setInput(output);
        setActiveTab(prev => (prev === 'encode' ? 'decode' : 'encode'));
    };

    const loadSample = () => {
        setInput(activeTab === 'encode' ? sampleText : sampleEncoded);
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="UTF-8 Encoder/Decoder - Free Online Text Encoding Tool">
                Encode and decode UTF-8 text instantly with our free online tool. Perfect for handling multilingual content and character encoding.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-6xl mx-auto mb-6">
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 h-12 bg-muted/50 backdrop-blur-sm">
                        <TabsTrigger
                            value="encode"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <Hash className="h-4 w-4 mr-2 text-emerald-500 group-data-[state=active]:animate-pulse" />
                            Encode
                        </TabsTrigger>
                        <TabsTrigger
                            value="decode"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <Type className="h-4 w-4 mr-2 text-blue-500 group-data-[state=active]:animate-pulse" />
                            Decode
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-4 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'encode' ? 'Original Text' : 'Encoded Text'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'encode'
                                            ? 'Enter your text to encode to UTF-8'
                                            : 'Paste UTF-8 encoded text to decode'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        activeTab === 'encode'
                                            ? 'e.g., Hello, こんにちは, 你好, مرحبًا, नमस्ते, Здравствуйте!'
                                            : 'Paste UTF-8 encoded text here...'
                                    }
                                    className="min-h-[300px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-2 lg:my-0">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent -z-10" />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleSwitch}
                            className="rounded-full w-14 h-14 shadow-lg hover:shadow-primary/20 hover:bg-primary/5 transition-all duration-300 bg-background border-2 border-primary/20 hover:border-primary/50 flex-shrink-0"
                            title="Switch between encode/decode"
                        >
                            <RefreshCw className="h-6 w-6 text-primary" />
                        </Button>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'encode' ? 'UTF-8 Encoded' : 'Decoded Text'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'encode'
                                            ? 'Your text encoded in UTF-8'
                                            : 'Your decoded text'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!output}
                                    className={cn(
                                        'gap-1.5 transition-all',
                                        isCopied
                                            ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                            : 'hover:bg-primary/5'
                                    )}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[300px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70'
                                    )}
                                    placeholder={
                                        activeTab === 'encode'
                                            ? 'Encoded UTF-8 will appear here...'
                                            : 'Decoded text will appear here...'
                                    }
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            {activeTab === 'encode'
                                                ? 'Your encoded text will appear here'
                                                : 'Your decoded text will appear here'}
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={utf8EncodeContent.title}
                    description={utf8EncodeContent.description}
                    content={utf8EncodeContent.content}
                    faqs={utf8EncodeContent.faqs}
                    relatedTools={utf8EncodeContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
